import { useState } from 'react';
import { Loader2, CalendarRange, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TimeOffRequestDialog } from '@/components/schedule/TimeOffRequestDialog';
import { PTO_TYPE_LABELS, formatDateShort } from './hrUtils';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { ScheduleGrid } from '@/components/schedule/ScheduleGrid';
import { WeekNav } from '@/components/schedule/WeekNav';
import { useWeekSchedule } from '@/components/schedule/useWeekSchedule';
import { shiftHours, fmtTime, typeMeta, DAY_NAMES } from '@/components/schedule/scheduleUtils';

export const MyScheduleTab = () => {
  const { user } = useAuth();
  const { monday, data, loading, reload, prevWeek, nextWeek, thisWeek } = useWeekSchedule(user?.id);
  const [requesting, setRequesting] = useState(false);
  const pending = data?.pending_requests || [];
  const shifts = data?.shifts || [];
  const timeOff = data?.time_off || [];
  const employees = data?.employees?.length ? data.employees : (user ? [{ id: user.id, name: user.name, role: user.role }] : []);
  const approvedOff = timeOff.filter((o) => o.status === 'approved');
  const hours = shifts.reduce((a, s) => a + shiftHours(s), 0);

  return (
    <div className="space-y-6" data-testid="hr-my-schedule-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200"><CardContent className="p-5">
          <p className="text-sm text-slate-500">Scheduled this week</p>
          <p className="text-3xl font-bold text-slate-800" data-testid="my-schedule-hours">{hours % 1 ? hours.toFixed(1) : hours}h</p>
        </CardContent></Card>
        <Card className="bg-white border-slate-200"><CardContent className="p-5">
          <p className="text-sm text-slate-500">Shifts</p>
          <p className="text-3xl font-bold text-slate-800" data-testid="my-schedule-shift-count">{shifts.length}</p>
        </CardContent></Card>
        <Card className="bg-white border-slate-200"><CardContent className="p-5">
          <p className="text-sm text-slate-500">Days off</p>
          <p className="text-3xl font-bold text-violet-600" data-testid="my-schedule-off-count">{approvedOff.length}</p>
        </CardContent></Card>
      </div>

      <WeekNav
        monday={monday} onPrev={prevWeek} onNext={nextWeek} onToday={thisWeek}
        right={(
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setRequesting(true)} data-testid="my-schedule-request-timeoff-btn">
            <Plane className="w-4 h-4 mr-1" /> Request time off
          </Button>
        )}
      />
      {pending.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="my-schedule-pending-timeoff">
          <p className="font-semibold mb-1">Awaiting approval</p>
          {pending.map((r) => (
            <p key={r.id}>{PTO_TYPE_LABELS[r.type] || r.type} · {formatDateShort(r.start_date)}{r.end_date !== r.start_date ? ` – ${formatDateShort(r.end_date)}` : ''}</p>
          ))}
        </div>
      )}
      <TimeOffRequestDialog open={requesting} onClose={() => setRequesting(false)} onSaved={reload} />

      {loading && !data ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
      ) : (
        <>
          <ScheduleGrid monday={monday} employees={employees} shifts={shifts} timeOff={timeOff} editable={false} showEmployeeColumn={false} />
          <div className="space-y-2" data-testid="my-schedule-list">
            {shifts.length === 0 && (
              <p className="text-sm text-slate-400 py-4 flex items-center gap-2"><CalendarRange className="w-4 h-4" /> No shifts scheduled this week.</p>
            )}
            {shifts.map((s) => {
              const meta = typeMeta(s.type);
              const d = new Date(s.date + 'T00:00:00');
              return (
                <div key={`${s.shift_id}-${s.date}`} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{DAY_NAMES[(d.getDay() + 6) % 7]} {d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · {fmtTime(s.start_time)} – {fmtTime(s.end_time)}</p>
                      <p className="text-xs text-slate-500">{s.label || meta.label}{s.notes ? ` · ${s.notes}` : ''}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.chip}`}>{shiftHours(s)}h</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
